import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, CreditCard, FileText, Lock, Check, Truck, Shield } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import StripePayment from '../components/StripePayment';

interface CheckoutForm {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  paymentMethod: 'card';
  cardNumber: string;
  cardExpiry: string;
  cardCvc: string;
  cardName: string;
}

const Checkout = () => {
  const { t } = useTranslation();
  const { items, total, clearCart, shipping: cartShipping } = useCart();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [stepErrors, setStepErrors] = useState<Record<string, string>>({});

  const validateStep = (step: number): boolean => {
    const errors: Record<string, string> = {};
    if (step === 1) {
      if (!form.firstName.trim()) errors.firstName = t('checkout.required');
      if (!form.lastName.trim()) errors.lastName = t('checkout.required');
      if (!form.email.trim()) errors.email = t('checkout.required');
      if (!form.phone.trim()) errors.phone = t('checkout.required');
    }
    if (step === 2) {
      if (!form.address.trim()) errors.address = t('checkout.required');
      if (!form.city.trim()) errors.city = t('checkout.required');
      if (!form.zipCode.trim()) errors.zipCode = t('checkout.required');
    }
    setStepErrors(errors);
    return Object.keys(errors).length === 0;
  };
  const [form, setForm] = useState<CheckoutForm>({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    paymentMethod: 'card',
    cardNumber: '',
    cardExpiry: '',
    cardCvc: '',
    cardName: ''
  });

  const shipping = cartShipping ?? 0;
  const finalTotal = total + shipping;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handlePaymentSuccess = (paymentId: string) => {
    setIsProcessing(false);
    clearCart();
    navigate(`/success?payment_intent=${paymentId}`);
  };

  const handlePaymentError = (error: string) => {
    console.error("❌ Erro no pagamento:", error);
    setPaymentError(error);
    setIsProcessing(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Payment is handled by StripePayment component
    // This form submit only validates the form fields
    if (form.paymentMethod === 'card') {
      // Stripe handles the payment via StripePayment component
      return;
    }
    // For non-card methods, show not yet available
    setPaymentError(t('checkout.paymentMethodNotAvailable'));
  };

  const steps = [
    { id: 1, name: t('checkout.step1'), icon: FileText },
    { id: 2, name: t('checkout.step2'), icon: Truck },
    { id: 3, name: t('checkout.step3'), icon: CreditCard }
  ];

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 pt-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('checkout.emptyCart')}</h2>
          <Link
            to="/produtos"
            className="inline-flex items-center bg-gradient-to-r from-[#4ECDC4] to-[#95C11F] text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {t('checkout.exploreProducts')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/cart"
            className="inline-flex items-center text-[#4ECDC4] hover:text-[#95C11F] transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {t('checkout.backToCart')}
          </Link>
          <h1 className="text-3xl lg:text-4xl font-bold text-gray-900">{t("checkout.title")}</h1>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center space-x-8">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className={`flex items-center justify-center w-12 h-12 rounded-full border-2 ${
                  currentStep >= step.id
                    ? 'bg-[#4ECDC4] border-[#4ECDC4] text-white'
                    : 'border-gray-300 text-gray-400'
                }`}>
                  {currentStep > step.id ? (
                    <Check className="w-6 h-6" />
                  ) : (
                    <step.icon className="w-6 h-6" />
                  )}
                </div>
                <span className={`ml-3 font-medium ${
                  currentStep >= step.id ? 'text-[#4ECDC4]' : 'text-gray-400'
                }`}>
                  {step.name}
                </span>
                {index < steps.length - 1 && (
                  <div className={`w-16 h-0.5 ml-8 ${
                    currentStep > step.id ? 'bg-[#4ECDC4]' : 'bg-gray-300'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Step 1: Personal Information */}
              {currentStep === 1 && (
                <div className="bg-white rounded-2xl shadow-lg p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('checkout.personalInfo')}</h2>
                  
                  {!isAuthenticated && (
                    <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-blue-800">
                        {t('checkout.haveAccount')}
                        <Link to="/login" className="font-medium text-[#4ECDC4] hover:text-[#95C11F] ml-1">
                          {t('checkout.loginHere')}
                        </Link>
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('checkout.firstName')} *
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={form.firstName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4ECDC4] focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('checkout.lastName')} *
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={form.lastName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4ECDC4] focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('checkout.email')} *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4ECDC4] focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('checkout.phone')} *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4ECDC4] focus:border-transparent"
                      />
                    </div>
                  </div>

                  {Object.keys(stepErrors).length > 0 && (
                    <p className="mt-2 text-sm text-red-600">{t('checkout.fillRequired')}</p>
                  )}
                  <button
                    type="button"
                    onClick={() => { if (validateStep(1)) { setStepErrors({}); setCurrentStep(2); } }}
                    className="mt-6 w-full bg-gradient-to-r from-[#4ECDC4] to-[#95C11F] text-white py-4 rounded-xl font-bold text-lg hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200"
                  >
                    {t('checkout.continue')}
                  </button>
                </div>
              )}

              {/* Step 2: Shipping Address */}
              {currentStep === 2 && (
                <div className="bg-white rounded-2xl shadow-lg p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('checkout.shippingAddress')}</h2>
                  
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('checkout.address')} *
                      </label>
                      <input
                        type="text"
                        name="address"
                        value={form.address}
                        onChange={handleInputChange}
                        required
                        placeholder={t('checkout.address')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4ECDC4] focus:border-transparent"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('checkout.city')} *
                        </label>
                        <input
                          type="text"
                          name="city"
                          value={form.city}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4ECDC4] focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('checkout.state')} *
                        </label>
                        <input
                          type="text"
                          name="state"
                          value={form.state}
                          onChange={handleInputChange}
                          placeholder={t('checkout.statePlaceholder')}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4ECDC4] focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('checkout.zipCode')} *
                        </label>
                        <input
                          type="text"
                          name="zipCode"
                          value={form.zipCode}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4ECDC4] focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex space-x-4 mt-6">
                    <button
                      type="button"
                      onClick={() => setCurrentStep(1)}
                      className="flex-1 border-2 border-[#4ECDC4] text-[#4ECDC4] py-4 rounded-xl font-bold text-lg hover:bg-[#4ECDC4] hover:text-white transition-all duration-200"
                    >
                      {t('checkout.previous')}
                    </button>
                    <button
                      type="button"
                      onClick={() => { if (validateStep(2)) { setStepErrors({}); setCurrentStep(3); } }}
                      className="flex-1 bg-gradient-to-r from-[#4ECDC4] to-[#95C11F] text-white py-4 rounded-xl font-bold text-lg hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200"
                    >
                      {t('checkout.continue')}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Payment Method */}
              {currentStep === 3 && (
                <div className="bg-white rounded-2xl shadow-lg p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('checkout.paymentMethod')}</h2>
                  
                  {/* Payment Method Selection */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <button
                      type="button"
                      onClick={() => setForm(prev => ({ ...prev, paymentMethod: 'card' }))}
                      className={`p-4 border-2 rounded-xl transition-all ${
                        form.paymentMethod === 'card'
                          ? 'border-[#4ECDC4] bg-[#4ECDC4] bg-opacity-10'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <CreditCard className="w-8 h-8 mx-auto mb-2 text-[#4ECDC4]" />
                      <span className="font-medium">{t('checkout.paymentMethod')}</span>
                    </button>
                    

                  </div>

                  {/* Credit Card Form */}
                  {form.paymentMethod === 'card' && (
                    <div className="space-y-6">
                      <div className="bg-gradient-to-r from-cyan-50 to-green-50 border border-cyan-200 rounded-lg p-6">
                        <div className="flex items-center mb-4">
                          <CreditCard className="w-6 h-6 text-cyan-600 mr-2" />
                          <h4 className="font-bold text-gray-900">{t('checkout.securePayment')}</h4>
                        </div>
                        <p className="text-gray-600 text-sm mb-4">{t('checkout.guaranteedSafe')}</p>
                        <StripePayment
                          total={finalTotal}
                          onSuccess={handlePaymentSuccess}
                          onError={handlePaymentError}
                          items={items}
                          shipping={shipping ?? 0}
                          billingDetails={{
                            name: `${form.firstName} ${form.lastName}`,
                            email: form.email,
                            phone: form.phone,
                            address: form.address,
                            city: form.city,
                            state: form.state,
                            zipCode: form.zipCode
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Payment Error */}
                  {paymentError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mt-4">
                      {t('checkout.paymentProcessingError')}: {paymentError}
                    </div>
                  )}                  <div className="flex space-x-4 mt-8">
                    <button
                      type="button"
                      onClick={() => setCurrentStep(2)}
                      className="flex-1 border-2 border-[#4ECDC4] text-[#4ECDC4] py-4 rounded-xl font-bold text-lg hover:bg-[#4ECDC4] hover:text-white transition-all duration-200"
                    >
                      {t('checkout.previous')}
                    </button>
                    <button
                      type="submit"
                      disabled={isProcessing}
                      className="flex-1 bg-gradient-to-r from-[#4ECDC4] to-[#95C11F] text-white py-4 rounded-xl font-bold text-lg hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {isProcessing ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                          {t('checkout.processing')}
                        </>
                      ) : (
                        <>
                          <Lock className="w-5 h-5 mr-2" />
                          {t("checkout.placeOrder")}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </form>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6">{t('checkout.orderTotal')}</h2>

              {/* Order Items */}
              <div className="space-y-4 mb-6">
                {items.map((item) => (
                  <div key={item.id} className="flex items-center space-x-4">
                    <img
                      src={item.product?.image_url || item.image_url}
                      alt={item.product?.name || item.name}
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900 text-sm">{item.product?.name || item.name}</h3>
                      <p className="text-gray-600 text-sm">{t('success.quantity')}: {item.quantity}</p>
                    </div>
                    <span className="font-bold text-[#4ECDC4]">
                      CHF {((item.product?.price || item.price || 0) * item.quantity).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>

              {/* Order Totals */}
              <div className="space-y-3 mb-6 border-t border-gray-200 pt-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('cart.subtotal')}</span>
                  <span className="font-medium">CHF {total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('cart.shipping')}</span>
                  <span className="font-medium">
                    {shipping === 0 ? (
                      <span className="text-green-600">{t('cart.freeShipping')}!</span>
                    ) : (
                      `CHF ${shipping.toFixed(2)}`
                    )}
                  </span>
                </div>
                <div className="border-t border-gray-200 pt-3">
                  <div className="flex justify-between text-lg font-bold">
                    <span>{t('cart.total')}</span>
                    <span className="text-[#4ECDC4]">CHF {finalTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Trust Indicators */}
              <div className="space-y-3">
                <div className="flex items-center text-sm text-gray-600">
                  <Shield className="w-4 h-4 text-[#4ECDC4] mr-2" />
                  {t('checkout.securePayment')}
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Truck className="w-4 h-4 text-[#4ECDC4] mr-2" />
                  {t('hero.freeShipping')}
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Lock className="w-4 h-4 text-[#4ECDC4] mr-2" />
                  {t('checkout.guaranteedSafe')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;